import { AfterViewInit, Component, DestroyRef, HostListener, OnInit, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AnonymousFeedbackComponent } from '@components/anonymous-feedback/anonymous-feedback.component';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { DateAdapter } from '@angular/material/core';
import { HttpClient } from '@angular/common/http';
import { BuildUpdateDialogComponent } from '@components/build-update-dialog/build-update-dialog.component';
import { AuthService } from '@auth/services/auth.service';
import { UserStore } from '@auth/services/user.store';
import { ru, kk, enGB } from 'date-fns/locale';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterModule, TranslateModule],
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly translateService = inject(TranslateService);
  public readonly ntf = inject(NotificationService);
  private readonly matDialog = inject(MatDialog);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly _adapter = inject<DateAdapter<unknown>>(DateAdapter);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly userStore = inject(UserStore);
  private readonly router = inject(Router);

  constructor() {
    effect(() => {
      if (!this.authService.isAuthenticated()) return;
      untracked(() => {
        if (this.userStore.user()) return;
        this.ntf.showLoader();
        this.userStore
          .ensureSession$()
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.ntf.hideLoader())
          )
          .subscribe(() => {
            if (this.userStore.user() && this.userStore.requiresRoleSelection()) {
              this.router.navigate(['/role-selection']);
            }
          });
      });
    });
  }

  ngOnInit(): void {
    if (window.location.href.includes('migration')) {
      this.googleAnalyticsService.initialize();
    }

    const lang = localStorage.getItem('language') || 'kz';
    this.setLang(lang);

    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.setLang(event.lang);
    });
  }

  private setLang(lang: string): void {
    this.translateService.use(lang);

    const dateLangMap: Record<string, any> = {
      kz: kk,
      ru: ru,
      en: enGB,
      ql: kk,
    };

    const dateLocale = dateLangMap[lang] || enGB;
    this._adapter.setLocale(dateLocale);
  }

  ngAfterViewInit(): void {
    this.checkBuildVersion();
  }

  private openUpdateDialog(): void {
    this.matDialog.open(BuildUpdateDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'update-warning-panel',
    });
  }

  private checkBuildVersion(): void {
    const localVersion = localStorage.getItem('buildVersion');
    this.http.get<{ buildVersion: string }>('/main-api/public/build-version').subscribe(({ buildVersion }) => {
      if (localVersion === buildVersion) return;
      if (localVersion) this.openUpdateDialog();
      localStorage.setItem('buildVersion', buildVersion);
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const mark = this.markContent();
    if (event.ctrlKey && event.key === 'Enter' && mark) {
      this.matDialog.open(AnonymousFeedbackComponent, {
        width: '35vw',
        data: mark,
      });
    }
  }

  private markContent(): string {
    const selection = window.getSelection();
    const textSeleted = selection?.toString();
    return textSeleted || '';
  }
}
